import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage, { ContactBlock, Section } from './LegalPage';
import COMPANY from '../legal/company';

const B = COMPANY.brand;
const mail = (address) => <a href={`mailto:${address}`}>{address}</a>;

export function TermsOfService() {
  return (
    <LegalPage
      title="Terms of Service"
      intro={`These terms are the agreement between your business and ${COMPANY.legalName} ("we", "us") for using ${B}. By creating an account or using ${B}, you accept them on behalf of your business.`}
    >
      <Section title="1. The service">
        <p>
          {B} is online software for manufacturing, processing and trading businesses: purchase, inventory, production,
          quality, sales and accounts, with GST features. We provide it as a service over the internet; we don&apos;t sell
          or install copies of it.
        </p>
      </Section>
      <Section title="2. Your account">
        <ul>
          <li>An account belongs to an organization (your business). The person who registers it is its Org Admin and can add users and give them roles.</li>
          <li>Keep passwords private. You are responsible for what is done by users of your organization, and for removing access when someone leaves.</li>
          <li>Give accurate details when you register, and keep them up to date. You must be at least 18 and able to act for your business.</li>
        </ul>
      </Section>
      <Section title="3. Plans, trials and fees">
        <ul>
          <li>Each plan sets its features and limits (users, locations, documents). The plan, its price and any onboarding fee are shown before you subscribe.</li>
          <li>Prices are exclusive of GST, which is added to the invoice. Fees are paid in advance for the plan period (monthly or yearly).</li>
          <li>A trial, where offered, is free for its stated period. When a paid period ends without renewal, you can still sign in, but you can&apos;t record new transactions until the plan is renewed.</li>
          <li>If you bought {B} through an authorised partner, the price and payment terms they agreed with you apply to that purchase.</li>
          <li>Cancellations and refunds are covered by our <Link to="/refund-policy">Refund &amp; Cancellation Policy</Link>.</li>
          <li>We may change plan prices; a change applies from your next renewal, and we will tell you before it does.</li>
        </ul>
      </Section>
      <Section title="4. Your data">
        <ul>
          <li>The business records you enter (items, vendors, customers, documents, ledgers and attachments) belong to your organization.</li>
          <li>You allow us to store and process them only to run, secure, back up and support {B} for you. We don&apos;t sell your data or use it for advertising.</li>
          <li>You are responsible for having the right to enter the personal details of your own contacts (such as vendors&apos; and customers&apos; names, phone numbers and addresses).</li>
          <li>You can download your records (for example as CSV files or a Tally export) while your account is active, and for {COMPANY.dataKeptDaysAfterEnd} days after it ends by asking us. After that we delete them, except where the law requires us to keep something.</li>
          <li>How we handle personal data is set out in our <Link to="/privacy">Privacy Policy</Link>.</li>
        </ul>
      </Section>
      <Section title="5. Acceptable use">
        <p>Don&apos;t use {B} to:</p>
        <ul>
          <li>break any law, including tax, GST and data protection law, or record transactions you know to be false;</li>
          <li>try to reach another organization&apos;s data, get around roles or plan limits, or test our security without our written permission;</li>
          <li>overload, copy, resell or reverse-engineer the service, or use automated means to access it except through features we provide;</li>
          <li>upload malware, or content you have no right to use.</li>
        </ul>
      </Section>
      <Section title="6. GST and compliance features">
        <p>
          {B} helps you prepare GST documents and returns (such as e-invoices, e-way bills, GSTR-1, GSTR-3B and the GSTR-2B
          reconciliation) from the records you enter. You remain responsible for checking them and for what you file. {B} is
          not tax, legal or accounting advice; please consult your accountant.
        </p>
      </Section>
      <Section title="7. Online stores and the shop page">
        <p>
          An organization that opens an online store is the seller of what it sells there. Its prices, descriptions, delivery, taxes, invoices, returns and refunds, and
          its compliance with the laws that apply to its goods (including consumer protection and e-commerce rules), are its own responsibility. {B} provides the
          software; it is not a party to a sale between a store and its buyers.
        </p>
        <p>
          The products page of ibigdo.com/shop shows products only from stores whose Org Admin has chosen to appear there, and they can stop at any time. A buyer completes every
          purchase in the seller&apos;s own store and pays the seller; {B} takes no payment for those sales and no commission on them. A seller must not list products that are illegal,
          unsafe, counterfeit or not as described. {B} may take a product or a store off the shop page, after a buyer&apos;s report or after its own review, without closing the store,
          and will say why.
        </p>
      </Section>
      <Section title="8. Availability, changes and support">
        <ul>
          <li>We work to keep {B} available and secure, but it may sometimes be unavailable, for example during maintenance or for reasons outside our control.</li>
          <li>We improve {B} regularly and may change or retire features. If a change removes something important you pay for, we will tell you in advance.</li>
          <li>Support is available through Help &amp; Support inside the app and by email at {mail(COMPANY.supportEmail)}.</li>
        </ul>
      </Section>
      <Section title="9. Suspension and ending">
        <ul>
          <li>You can stop using {B} at any time; see the <Link to="/refund-policy">Refund &amp; Cancellation Policy</Link>.</li>
          <li>We may suspend or close an account that breaks these terms, puts the service or other customers at risk, or remains unpaid. Where we reasonably can, we will warn you first and give you time to put it right.</li>
        </ul>
      </Section>
      <Section title="10. Liability">
        <ul>
          <li>{B} is provided &quot;as is&quot;. To the extent the law allows, we don&apos;t give warranties beyond those in these terms.</li>
          <li>We are not liable for indirect or consequential loss, such as lost profit, lost business or penalties arising from returns you filed.</li>
          <li>Our total liability for any claim is limited to the fees you paid us for {B} in the 12 months before the claim.</li>
          <li>Nothing here limits liability that cannot be limited by law.</li>
        </ul>
      </Section>
      <Section title="11. Changes to these terms">
        <p>
          We may update these terms. The date at the top shows the latest version. For a significant change we will tell your
          Org Admin by email or in the app before it applies. Continuing to use {B} after that means you accept the change.
        </p>
      </Section>
      <Section title="12. Law and disputes">
        <p>
          These terms are governed by the laws of India. Disputes are subject to the exclusive jurisdiction of the courts at{' '}
          {COMPANY.jurisdictionCity}. Before going to court, please write to us so we can try to resolve it.
        </p>
      </Section>
      <Section title="13. Contact">
        <ContactBlock />
      </Section>
    </LegalPage>
  );
}

export function PrivacyPolicy() {
  const officer = COMPANY.grievanceOfficer;
  return (
    <LegalPage
      title="Privacy Policy"
      intro={`This policy explains what personal data ${COMPANY.legalName} collects through ${B}, why, who it is shared with, and the rights you have, including under India's Digital Personal Data Protection Act, 2023.`}
    >
      <Section title="1. Two kinds of data">
        <ul>
          <li><strong>Account data</strong>: about the people who use {B} (name, email address, phone number, organization, roles, sign-in records). We decide how this is used.</li>
          <li><strong>Business records</strong>: what your organization enters (vendors, customers, items, documents, ledgers, photos of slips). Some contain personal details of your contacts. Your organization decides what is entered and why; we process it only to provide {B} to you.</li>
        </ul>
      </Section>
      <Section title="2. What we collect">
        <ul>
          <li>When you register or are added as a user: name, email address, phone number (if given), organization name and currency, and a password (stored only as a one-way hash). Your organization&apos;s address, GSTIN and similar details, if your Org Admin adds them.</li>
          <li>When you contact us, use Help &amp; Support or send feedback: your message, name and email address.</li>
          <li>When you use {B}: sign-in times, role changes and similar records needed for security and support.</li>
          <li>When you pay online: the payment is handled by our payment gateway. We receive a payment reference and status, never your card or bank login details.</li>
        </ul>
      </Section>
      <Section title="3. Why we use it">
        <ul>
          <li>to create and run your account and organization, and sign you in securely;</li>
          <li>to send messages about your account: password resets, email verification, payment reminders you set up, and notices about your plan or the service;</li>
          <li>to answer support requests and improve {B};</li>
          <li>to bill for plans and meet our legal, tax and accounting duties.</li>
        </ul>
        <p>We use account data on the basis of your consent when you register, and for the legitimate uses the law allows. We don&apos;t sell personal data or use it for advertising.</p>
      </Section>
      <Section title="4. Who we share it with">
        <ul>
          <li><strong>Service providers</strong> that run parts of {B} for us under contract: hosting and storage, email delivery, and the payment gateway.</li>
          <li><strong>GST services</strong>, when you use them: a GSTIN you look up is checked with a GST data provider, and e-invoice or e-way bill details are sent to the government portals through an authorised provider.</li>
          <li><strong>Your partner</strong>: if an authorised {B} partner manages your account, they see the account and plan details they need to support you.</li>
          <li><strong>Authorities</strong>, when the law requires it.</li>
        </ul>
        <p>Our providers may only use the data to provide their service to us.</p>
      </Section>
      <Section title="5. Cookies and browser storage">
        <p>
          {B} keeps a few items in your browser so it works: your sign-in session, your theme (light or dark) and layout
          choices. We don&apos;t use advertising or third-party tracking cookies.
        </p>
      </Section>
      <Section title="6. How long we keep it">
        <ul>
          <li>Account data and business records: while the organization&apos;s account is active, and for {COMPANY.dataKeptDaysAfterEnd} days after it ends so you can ask for an export. We then delete them, except what the law requires us to keep (for example, our own invoices to you).</li>
          <li>Backups are overwritten on a rolling schedule.</li>
        </ul>
      </Section>
      <Section title="7. Your rights">
        <p>You can ask us to:</p>
        <ul>
          <li>tell you what personal data we hold about you and how it is used;</li>
          <li>correct or complete it, or delete it when it is no longer needed;</li>
          <li>withdraw your consent (this may mean you can no longer use {B});</li>
          <li>let someone you nominate exercise these rights if you die or become unable to.</li>
        </ul>
        <p>
          If you are a vendor or customer of one of our users and your details are in their records, please ask that
          business first; we will help them respond.
        </p>
      </Section>
      <Section title="8. Grievance officer">
        <p>For questions or complaints about your personal data, contact our grievance officer:</p>
        <p className="mb-2">{officer.name}<br />Email: {mail(officer.email)}<br />{COMPANY.address}</p>
        <p>
          We will acknowledge your complaint and try to resolve it within 30 days. If you are not satisfied, you may complain
          to the Data Protection Board of India.
        </p>
      </Section>
      <Section title="9. Security">
        <p>How we protect data is described on our <Link to="/security">Security &amp; Data</Link> page.</p>
      </Section>
      <Section title="10. Children">
        <p>{B} is for businesses and is not meant for anyone under 18. We don&apos;t knowingly collect their data.</p>
      </Section>
      <Section title="11. Changes">
        <p>We may update this policy. The date at the top shows the latest version, and we will tell users of significant changes.</p>
      </Section>
      <Section title="12. Contact">
        <ContactBlock />
      </Section>
    </LegalPage>
  );
}

export function RefundPolicy() {
  return (
    <LegalPage
      title="Refund & Cancellation"
      intro={`How to cancel a ${B} plan, and when money is refunded.`}
    >
      <Section title="1. Free trial">
        <p>Where a free trial is offered, nothing is charged during it. If you don&apos;t subscribe before it ends, recording new transactions stops; nothing is billed.</p>
      </Section>
      <Section title="2. Cancelling">
        <ul>
          <li>You can cancel at any time. Ask your Org Admin to write to {mail(COMPANY.supportEmail)} from the account&apos;s email address, or use Help &amp; Support in the app.</li>
          <li>Cancelling stops the next renewal. Your plan keeps working until the end of the period already paid for.</li>
          <li>After that you can still sign in, and you can ask for an export of your data for {COMPANY.dataKeptDaysAfterEnd} days, as set out in the <Link to="/terms">Terms of Service</Link>.</li>
        </ul>
      </Section>
      <Section title="3. Refunds">
        <ul>
          <li>Plan fees are not refundable, including for the unused part of a monthly or yearly period, or for features you didn&apos;t use.</li>
          <li>Onboarding and set-up fees are not refundable once onboarding has started.</li>
          <li>Moving to a lower plan takes effect from the next renewal; the difference is not refunded.</li>
        </ul>
      </Section>
      <Section title="4. When we do refund">
        <ul>
          <li>You were charged twice for the same period.</li>
          <li>You were charged for a renewal after you had cancelled in time.</li>
          <li>We charged you in error, or could not provide the service you paid for because of a fault on our side.</li>
        </ul>
        <p>
          Write to {mail(COMPANY.supportEmail)} within 30 days of the charge with your organization name and the payment
          reference. Once approved, the refund is sent to the original payment method within 7 working days; your bank may take a
          few more days to show it.
        </p>
      </Section>
      <Section title="5. Bought through a partner">
        <p>If you paid an authorised {B} partner rather than us, please contact that partner first; their terms with you apply to that payment.</p>
      </Section>
      <Section title="6. Contact">
        <ContactBlock />
      </Section>
    </LegalPage>
  );
}

export function SecurityAndData() {
  return (
    <LegalPage
      title="Security & Data"
      intro={`Your purchase, stock, sales and accounts records are the heart of your business. This is how ${B} keeps them safe, and how you keep control of them.`}
    >
      <Section title="Each organization is kept separate">
        <p>Every record belongs to one organization, and every request is checked against the organization of the person signed in, so one business can never see another&apos;s data.</p>
      </Section>
      <Section title="Access by role">
        <ul>
          <li>Your Org Admin gives each user only the roles they need: purchase, weighbridge, inventory, production, quality, sales or finance, as operator or manager.</li>
          <li>Each screen and action is checked on our servers, not only hidden in the app.</li>
          <li>Every role given or taken away is recorded, and an organization can&apos;t be left without an active Org Admin.</li>
        </ul>
      </Section>
      <Section title="Sign-in">
        <p>Passwords are stored only as a one-way (bcrypt) hash, so nobody, including us, can read them. Password resets go only to the user&apos;s registered email address.</p>
      </Section>
      <Section title="A record you can trust">
        <p>Posted documents are cancelled with a reason rather than deleted, and their ledger entries are reversed, so your books keep a complete trail of what happened and when.</p>
      </Section>
      <Section title="Encrypted connections">
        <p>Everything between your browser and {B} travels encrypted (HTTPS).</p>
      </Section>
      <Section title="Your data stays yours">
        <ul>
          <li>Download your lists and registers as CSV files, and export vouchers to Tally for your accountant.</li>
          <li>We don&apos;t sell your data or use it for advertising. See the <Link to="/privacy">Privacy Policy</Link> for who we share data with and why.</li>
          <li>If you leave, you can ask for an export for {COMPANY.dataKeptDaysAfterEnd} days, after which your data is deleted.</li>
        </ul>
      </Section>
      <Section title="Found a security problem?">
        <p>Please tell us at {mail(COMPANY.supportEmail)} before sharing it with anyone else. We will look into it straight away and keep you informed.</p>
      </Section>
    </LegalPage>
  );
}
